/**
 * Rotating minimap + heading compass.
 *
 * The level silhouette is rasterised exactly once at startup from
 * `ctx.physics.colliders` into an offscreen canvas. Every frame after that is a
 * single rotated `drawImage` blit of that cached bitmap plus a handful of vector
 * blips — which is why this stays comfortably inside the frame budget even though
 * it is the only part of the HUD that repaints continuously.
 *
 * ## What puts a red dot on the map
 *
 * A hostile who has **given their own position away**, and nothing else:
 *
 *   - they fired, and the weapon was loud enough to locate (a suppressor really
 *     does hide you here — the reveal scales with the firing signature);
 *   - they were hit, so you already know where they are;
 *   - a recon streak is up. UAV and Scout Pulse exist to sell exactly this, and
 *     they would be worth nothing if the map gave it away for free.
 *
 * It used to also paint anything in line of sight, which sounds cautious and is
 * the opposite: a contact you can see is a contact already on your screen, so the
 * dot told you nothing you did not have — and it told you about people who had
 * done nothing to earn it, including through the corner of your eye at 60 m.
 *
 * Hostiles only. With teams on the map, "everything that is not the player" stopped
 * meaning "the enemy": it would paint your own squad in enemy red.
 */

import * as THREE from 'three';
import { MASK } from '../world/Physics.js';

export const TUNING = {
  // CSS px. Must match `--mm` in hud.css: the canvas is drawn in this coordinate
  // space and the wrapper stretches it to 100%, so a mismatch silently rescales
  // every blip. The rest of the top-left column is positioned off `--mm` too.
  size: 168,
  pxPerMetre: 4.6, // ~18 m of visible radius — tactical, not strategic
  maxOffscreen: 1600, // px; scale is reduced rather than exceed this
  minWallHeight: 0.9, // m — anything flatter is floor or kerb, not silhouette
  minFootprint: 0.35, // m — drops poles, railings and cable clutter
  hugeFootprint: 0.34, // fraction of level area above which a collider is "ground"
  redrawHz: 30,

  losHz: 8,
  losPerPass: 4,
  revealTime: 2.2, // s a contact stays on the map after being spotted
  fireReveal: 3.0, // s after a contact fires, at full firing signature
  // Below this signature a shot is silent as far as the map is concerned. A blip
  // that lives for a tenth of a second reads as a rendering fault, not as intel.
  minFireSignature: 0.08,
  // ...and above this it stops paying. A brake at 1.25 holds the contact for 3.75 s
  // instead of 3.0; anything past 2 would keep a whole firefight permanently lit.
  maxFireSignature: 2,

  compassPxPerDeg: 2.2,
  compassStep: 15,
  hitMarkLife: 2.4,
  hitMarks: 4,
};

const D2R = Math.PI / 180;
const CARD = { 0: 'N', 45: 'NE', 90: 'E', 135: 'SE', 180: 'S', 225: 'SW', 270: 'W', 315: 'NW' };

export class Minimap {
  constructor(ctx, root) {
    this.ctx = ctx;

    const wrap = document.createElement('div');
    wrap.className = 'hud-minimap hud-fade';
    this.canvas = document.createElement('canvas');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.dpr = dpr;
    this.canvas.width = TUNING.size * dpr;
    this.canvas.height = TUNING.size * dpr;
    wrap.appendChild(this.canvas);
    root.appendChild(wrap);
    this.g = this.canvas.getContext('2d');
    this.g.scale(dpr, dpr);

    this.R = TUNING.size / 2;
    this.scale = TUNING.pxPerMetre;
    this.static = null; // { canvas, minX, minZ, scale }
    this._acc = 0;
    this._losAcc = 0;
    this._losCursor = 0;
    this.reveal = new Map(); // actor id -> seconds remaining

    this._buildCompass(root);
    this._bind();

    // Build the silhouette after the level has finished registering colliders.
    this._built = false;
  }

  _bind() {
    const ev = this.ctx.events;
    ev.on('hit:actor', (p) => this._reveal(p?.actor, TUNING.revealTime));
    // The AI module may or may not exist yet; subscribing costs nothing.
    ev.on('ai:fire', (p) => this._reveal(p?.actor, TUNING.fireReveal));
    ev.on('enemy:fire', (p) => this._reveal(p?.actor, TUNING.fireReveal));

    /**
     * A shot is only intel if the weapon that fired it advertises itself.
     *
     * `combat:shot` carries a `signature` — see `src/game/behaviours/Signature.js`
     * — and the reveal window scales with it, so a suppressor is not a special case
     * in this file: it is the value 0 arriving on the same event every other shot
     * arrives on. Below `minFireSignature` the shot buys the map nothing at all,
     * which is what stops a heavily-suppressed weapon leaving a three-frame ghost
     * blip that reads as a bug.
     *
     * The scale runs *past* 1, up to `maxFireSignature`. A muzzle brake is louder
     * than stock and that has to cost its owner something real; capping the window
     * at the stock value would make "louder" a modifier that cannot be paid for,
     * which is worse than not offering it.
     *
     * The player is skipped: their own position is the arrow in the middle.
     */
    ev.on('combat:shot', (p) => {
      if (!p || p.isPlayer) return;
      const raw = typeof p.signature === 'number' ? p.signature : 1;
      if (raw < TUNING.minFireSignature) return;
      const sig = Math.min(raw, TUNING.maxFireSignature);
      this._reveal(p.actor, TUNING.fireReveal * sig);
    });
  }

  _reveal(actor, t) {
    const id = actor?.id ?? actor;
    if (id === undefined || id === null) return;
    /**
     * Hostiles only, refused at the door rather than filtered at the draw.
     *
     * `weapon:fire` and `hit:actor` fire for everybody now, so a squadmate letting
     * off a burst was putting an entry in here. The draw loop walks the hostile
     * list and would never have painted it, so nothing was visibly wrong — which is
     * exactly why it is worth closing: a map of "revealed contacts" that quietly
     * contains your own team is a trap for whatever reads it next.
     */
    const me = this.ctx.player;
    const match = this.ctx.match;
    if (match && me && typeof actor === 'object' && !match.hostile(me, actor)) return;
    const cur = this.reveal.get(id) ?? 0;
    if (t > cur) this.reveal.set(id, t);
  }

  /* ------------------------------ silhouette ------------------------------ */

  build() {
    const colliders = this.ctx.physics?.colliders;
    if (!colliders || !colliders.length) return false;

    let walls = colliders.filter(
      (c) =>
        c.max.y - c.min.y >= TUNING.minWallHeight &&
        Math.min(c.max.x - c.min.x, c.max.z - c.min.z) >= TUNING.minFootprint
    );
    if (walls.length < 4) walls = colliders;

    let b = bounds(walls);
    const area = Math.max(1, (b.maxX - b.minX) * (b.maxZ - b.minZ));
    const trimmed = walls.filter(
      (c) => (c.max.x - c.min.x) * (c.max.z - c.min.z) < area * TUNING.hugeFootprint
    );
    if (trimmed.length > 4) {
      walls = trimmed;
      b = bounds(walls);
    }

    const wM = b.maxX - b.minX;
    const hM = b.maxZ - b.minZ;
    let s = TUNING.pxPerMetre;
    const longest = Math.max(wM, hM) * s;
    if (longest > TUNING.maxOffscreen) s *= TUNING.maxOffscreen / longest;
    this.scale = s;

    const pad = 6;
    const cw = Math.max(8, Math.ceil(wM * s) + pad * 2);
    const ch = Math.max(8, Math.ceil(hM * s) + pad * 2);
    const off = document.createElement('canvas');
    off.width = cw;
    off.height = ch;
    const g = off.getContext('2d');

    // Outline-led rather than fill-led: at 130 px across, an edge reads as
    // architecture where a solid block reads as noise.
    g.fillStyle = 'rgba(233,238,242,0.10)';
    g.strokeStyle = 'rgba(233,238,242,0.62)';
    g.lineWidth = 1;
    for (const c of walls) {
      const x = (c.min.x - b.minX) * s + pad;
      const y = (c.min.z - b.minZ) * s + pad;
      const w = Math.max(1, (c.max.x - c.min.x) * s);
      const h = Math.max(1, (c.max.z - c.min.z) * s);
      g.fillRect(x, y, w, h);
      g.strokeRect(x + 0.5, y + 0.5, Math.max(0, w - 1), Math.max(0, h - 1));
    }

    this.static = { canvas: off, minX: b.minX - pad / s, minZ: b.minZ - pad / s, scale: s };
    return true;
  }

  /* -------------------------------- compass ------------------------------- */

  _buildCompass(root) {
    const wrap = document.createElement('div');
    wrap.className = 'hud-compass hud-fade';

    const strip = document.createElement('div');
    strip.className = 'hud-compass-strip';
    const ppd = TUNING.compassPxPerDeg;
    for (let h = -90; h <= 450; h += TUNING.compassStep) {
      const m = document.createElement('div');
      const norm = ((h % 360) + 360) % 360;
      const label = CARD[norm];
      if (label) {
        m.className = label.length === 1 ? 'cp-m cp-card' : 'cp-m';
        m.textContent = label;
      } else {
        m.className = 'cp-m cp-minor';
      }
      m.style.left = `${(h * ppd).toFixed(1)}px`;
      strip.appendChild(m);
    }
    wrap.appendChild(strip);

    const pin = document.createElement('div');
    pin.className = 'hud-compass-pin';
    wrap.appendChild(pin);

    this.marks = [];
    for (let i = 0; i < TUNING.hitMarks; i++) {
      const el = document.createElement('div');
      el.className = 'hud-compass-mark';
      wrap.appendChild(el);
      this.marks.push({ el, age: -1, abs: 0 });
    }

    root.appendChild(wrap);
    this.compass = wrap;
    this.strip = strip;
    this._wroteStrip = -9999;
    this._markNext = 0;
  }

  /** Called by the HUD with a view-relative bearing in degrees. */
  addHitMark(bearingDeg) {
    const heading = this._heading();
    let slot = this.marks.find((m) => m.age < 0) ?? this.marks[this._markNext++ % this.marks.length];
    slot.age = 0;
    slot.abs = heading + bearingDeg;
  }

  _heading() {
    const yaw = this.ctx.player?.yaw ?? 0;
    return -yaw / D2R;
  }

  /* --------------------------------- frame -------------------------------- */

  update(dt) {
    if (!this._built) this._built = this.build();

    const p = this.ctx.player;
    const yaw = p?.yaw ?? 0;

    this._updateCompass(dt, yaw);

    // decay reveals
    if (this.reveal.size) {
      for (const [id, t] of this.reveal) {
        const n = t - dt;
        if (n <= 0) this.reveal.delete(id);
        else this.reveal.set(id, n);
      }
    }

    /**
     * The line-of-sight sweep is gone from the frame loop.
     *
     * It painted every hostile you could see, which is the one group that needs no
     * painting — they are already on your screen — while quietly making the recon
     * streaks worthless, because the map was doing their job for free. What is left
     * is intel a hostile handed over: a shot, a hit, or a UAV overhead.
     *
     * The method is kept (`_lineOfSight`) rather than deleted, because a mode that
     * wants a radar-style map is a rules decision and this is where it would hook
     * back in.
     */

    this._acc += dt;
    if (this._acc < 1 / TUNING.redrawHz) return;
    this._acc = 0;
    this._draw(yaw);
  }

  _updateCompass(dt, yaw) {
    const heading = -yaw / D2R;
    const ppd = TUNING.compassPxPerDeg;
    const px = -heading * ppd;
    if (Math.abs(px - this._wroteStrip) > 0.3) {
      this.strip.style.transform = `translateX(${px.toFixed(1)}px)`;
      this._wroteStrip = px;
    }
    for (const m of this.marks) {
      if (m.age < 0) continue;
      m.age += dt;
      if (m.age >= TUNING.hitMarkLife) {
        m.age = -1;
        m.el.style.opacity = '0';
        continue;
      }
      let d = m.abs - heading;
      d = ((((d + 180) % 360) + 360) % 360) - 180;
      const o = 1 - m.age / TUNING.hitMarkLife;
      m.el.style.transform = `translateX(${(d * ppd).toFixed(1)}px)`;
      m.el.style.opacity = o.toFixed(2);
    }
  }

  /**
   * Everyone the player is allowed to see a red dot for.
   *
   * Asks the match, so it covers bots *and* other humans and excludes your own
   * side. The `ai.enemies` fallback is only for a match with no authority at all;
   * it was the primary path once, and once teams existed it was quietly wrong,
   * because that list holds both sides.
   */
  _enemies() {
    const match = this.ctx.match;
    const me = this.ctx.player;
    this._scratch = this._scratch || [];
    if (match && me) {
      this._scratch.length = 0;
      for (const reg of match.actors.values()) {
        if (reg.dead || reg.actor === me) continue;
        const a = reg.actor;
        if (!a?.position || typeof a.health !== 'number') continue;
        if (!match.hostile(me, a)) continue;
        this._scratch.push(a);
      }
      return this._scratch;
    }
    const ai = this.ctx.ai;
    if (Array.isArray(ai?.enemies)) return ai.enemies;
    const actors = this.ctx.physics?.actors;
    if (actors && actors.size) {
      this._scratch.length = 0;
      for (const a of actors.values()) if (a?.id !== 'player') this._scratch.push(a);
      return this._scratch;
    }
    return null;
  }

  _lineOfSight() {
    const list = this._enemies();
    const p = this.ctx.player;
    const phys = this.ctx.physics;
    if (!list || !list.length || !p || !phys?.raycast) return;

    const eye = _v.set(p.position.x, p.position.y + (p.eye ?? 1.65), p.position.z);
    for (let n = 0; n < TUNING.losPerPass; n++) {
      const a = list[this._losCursor++ % list.length];
      if (!a || a.dead || !a.position) continue;
      const dx = a.position.x - eye.x;
      const dy = a.position.y + 1.1 - eye.y;
      const dz = a.position.z - eye.z;
      const dist = Math.hypot(dx, dy, dz);
      if (dist < 0.1 || dist > 90) continue;
      _d.set(dx / dist, dy / dist, dz / dist);
      const r = phys.raycast(eye, _d, dist - 0.3, MASK.WORLD | MASK.PROP);
      if (r?.hit) continue;
      // Smoke is a sight blocker, not a bullet blocker, so it cannot live in the
      // raycast masks — `physics.raycast` is also how rounds travel. Every other
      // line-of-sight consumer in the game goes through `physics.visible`, which
      // the smoke field wraps; this one is a raycast, so it asks directly.
      // Without this the map would keep painting contacts you have just smoked off.
      const smoke = this.ctx.behaviours?.smoke;
      if (smoke && smoke.count > 0) {
        _t.set(a.position.x, a.position.y + 1.1, a.position.z);
        if (smoke.blocks(eye, _t)) continue;
      }
      this._reveal(a, TUNING.revealTime);
    }
  }

  _draw(yaw) {
    const g = this.g;
    const R = this.R;
    const S = this.static;
    g.clearRect(0, 0, TUNING.size, TUNING.size);

    g.save();
    g.beginPath();
    g.arc(R, R, R - 0.5, 0, Math.PI * 2);
    g.clip();

    const p = this.ctx.player;
    const px = p?.position?.x ?? 0;
    const pz = p?.position?.z ?? 0;
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);

    if (S) {
      const ux = (px - S.minX) * S.scale;
      const uz = (pz - S.minZ) * S.scale;
      g.save();
      g.translate(R, R);
      g.rotate(yaw);
      g.translate(-ux, -uz);
      g.drawImage(S.canvas, 0, 0);
      g.restore();
    }

    // field-of-view wedge
    g.save();
    g.translate(R, R);
    g.beginPath();
    g.moveTo(0, 0);
    g.arc(0, 0, R * 0.92, -Math.PI / 2 - 0.62, -Math.PI / 2 + 0.62);
    g.closePath();
    g.fillStyle = 'rgba(255,138,61,0.07)';
    g.fill();
    g.restore();

    // Contacts. A 2.4 px dot on a map with 1 px wall outlines on it is the same
    // size as the architecture and gets lost in it; a contact has to be the one
    // thing on this disc that is unambiguously *not* geometry. So it is drawn
    // larger, in the only saturated red on the HUD, with a dark keyline and a
    // soft halo underneath — the same outline-first trick the rest of the HUD
    // uses, because the map's own background is mixed light and dark.
    // Recon scorestreaks (Scout Pulse, UAV, Gunship) publish `ctx.intel`. They are
    // an *additional* reveal source, not a replacement: the base rules — line of
    // sight, and recently-fired — still stand, so a UAV adds intel rather than
    // switching the minimap on. This is the whole payoff of the recon tier.
    const intel = this.ctx.intel;
    const list = this._enemies();
    if (list && (this.reveal.size || intel?.revealAll || intel?.ids?.size)) {
      const sc = S ? S.scale : this.scale;
      for (const a of list) {
        if (!a?.position || a.dead) continue;
        const key = a.id ?? a;
        if (!this.reveal.has(key) && !intel?.revealAll && !intel?.ids?.has(key)) continue;
        const dx = a.position.x - px;
        const dz = a.position.z - pz;
        let bx = (dx * c - dz * s) * sc;
        let by = (dx * s + dz * c) * sc;
        const len = Math.hypot(bx, by);
        let edge = false;
        if (len > R - 7) {
          const k = (R - 7) / len;
          bx *= k;
          by *= k;
          edge = true;
        }
        const r = edge ? 2.4 : 3.4;
        g.beginPath();
        g.arc(R + bx, R + by, r + 1.9, 0, Math.PI * 2);
        g.fillStyle = 'rgba(6,3,3,0.5)';
        g.fill();
        g.beginPath();
        g.arc(R + bx, R + by, r, 0, Math.PI * 2);
        g.fillStyle = edge ? 'rgba(255,90,74,0.7)' : '#ff4b3e';
        g.fill();
        g.lineWidth = 1;
        g.strokeStyle = 'rgba(5,7,10,0.85)';
        g.stroke();
      }
    }

    g.restore();

    // player arrow
    g.save();
    g.translate(R, R);
    g.beginPath();
    g.moveTo(0, -5.8);
    g.lineTo(4.2, 4.6);
    g.lineTo(0, 2.6);
    g.lineTo(-4.2, 4.6);
    g.closePath();
    g.fillStyle = '#ff8a3d';
    g.strokeStyle = 'rgba(5,7,10,0.85)';
    g.lineWidth = 1;
    g.fill();
    g.stroke();
    g.restore();

    // north pip on the bezel
    g.save();
    g.translate(R, R);
    g.rotate(yaw);
    g.fillStyle = 'rgba(233,238,242,0.7)';
    g.fillRect(-0.9, -R + 1.5, 1.8, 4.5);
    g.restore();
  }
}

function bounds(list) {
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (const c of list) {
    if (c.min.x < minX) minX = c.min.x;
    if (c.min.z < minZ) minZ = c.min.z;
    if (c.max.x > maxX) maxX = c.max.x;
    if (c.max.z > maxZ) maxZ = c.max.z;
  }
  return { minX, minZ, maxX, maxZ };
}

// Reused vectors so the line-of-sight sweep allocates nothing per frame.
const _v = new THREE.Vector3();
const _d = new THREE.Vector3();
const _t = new THREE.Vector3();
